import { API_BASE, SUPER_ADMIN_EMAIL } from './config';
import React, { useState, useEffect, useCallback, createContext, useContext, Component } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { loginWithSupabase, logoutWithSupabase } from './utils/supabaseAuthClient';
import { useTranslation } from './i18n/LanguageContext';
import LanguageSelector from './components/LanguageSelector';
import { Clock, BarChart3, Users, Shield, Home } from 'lucide-react';
import './App.css';
import AppRoutes from './components/AppRoutes';
import LandingPage from './components/LandingPage';
import DocumentationPage from './components/StaticPages/DocumentationPage';
import ContactPage from './components/StaticPages/ContactPage';
import FAQPage from './components/StaticPages/FAQPage';
import CGUPage from './components/StaticPages/CGUPage';
import ConfidentialityPage from './components/StaticPages/ConfidentialityPage';
import LegalNoticePage from './components/StaticPages/LegalNoticePage';
import { useSessionTimeout } from './hooks/useSessionTimeout';
import PWAInstallBanner from './components/PWAInstallBanner';
import { unregisterWebPushForCurrentUser } from './utils/pushNotifications';
// Context d'authentification
export const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return context;
};

// Provider d'authentification
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [dashboardStats, setDashboardStats] = useState({ leaveDays: 0, hoursWorked: 0, attendanceRate: 0 });
  const refreshInFlight = React.useRef(false);
  const lastRefreshTime = React.useRef(0);

  // Fonction pour déconnecter l'utilisateur et nettoyer la session
  const handleSessionExpire = () => {
    console.log('🔒 Session expirée après 30 minutes d\'inactivité - Déconnexion automatique');
    localStorage.removeItem('token');
    setUser(null);
    
    // Afficher un message d'alerte
    alert(t('auth.session_expired'));
    
    // Rediriger vers la page website (landing)
    window.location.href = '/';
  };

  // Hook pour gérer l'expiration de session (30 minutes d'inactivité)
  // Ne s'active que si l'utilisateur est connecté
  useSessionTimeout(30, user ? handleSessionExpire : null);

  const navigate = useNavigate();
  const { t } = useTranslation();

  // ✅ NOUVEAU : Fonction pour créer automatiquement un profil employé
  const createEmployeeProfileIfNeeded = useCallback(async (token, user) => {
    try {
      // Vérifier d'abord si l'utilisateur a déjà un profil employé
      const checkResponse = await fetch(`${API_BASE}/api/employees/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Si l'utilisateur a déjà un profil employé, ne rien faire
      if (checkResponse.ok) {
        const checkData = await checkResponse.json();
        if (checkData.success && checkData.employee) {
          console.log('✅ Profil employé déjà existant');
          return;
        }
      }

      // Si pas de profil employé, en créer un automatiquement
      console.log('📝 Création automatique du profil employé...');
      const createResponse = await fetch(`${API_BASE}/api/auth/create-employee-profile`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (createResponse.ok) {
        const createData = await createResponse.json();
        if (createData.success) {
          console.log('✅ Profil employé créé automatiquement:', createData.employee?.employee_code);
        }
      } else {
        console.warn('⚠️ Impossible de créer le profil employé automatiquement');
      }
    } catch (error) {
      console.error('❌ Erreur création profil employé:', error);
      // Ne pas bloquer l'application si la création échoue
    }
  }, []);

  const verifyToken = useCallback(async (token) => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        setUser(data.user);
        
        // ✅ NOUVEAU : Créer automatiquement un profil employé si l'utilisateur n'en a pas
        if (data.user) {
          createEmployeeProfileIfNeeded(token, data.user);
        }
        } else {
          // Réponse non-JSON, token probablement invalide
          localStorage.removeItem('token');
          setUser(null);
          navigate('/', { replace: true });
        }
      } else {
        localStorage.removeItem('token');
        setUser(null);
        navigate('/', { replace: true });
      }
    } catch (error) {
      console.error('❌ Erreur vérification token:', error);
      localStorage.removeItem('token');
      setUser(null);
      navigate('/', { replace: true });
    } finally {
      setLoading(false);
    }
  }, [createEmployeeProfileIfNeeded, navigate]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      verifyToken(token);
    } else {
      setLoading(false);
    }
  }, [verifyToken]);

  const register = async (userData) => {
    try {
      setError('');

      let csrfToken = '';
      try {
        const csrfRes = await fetch(`${API_BASE}/api/auth/csrf-token`, { method: 'GET' });
        if (csrfRes.ok) {
          const csrfData = await csrfRes.json();
          csrfToken = csrfData.csrfToken || '';
        }
      } catch (e) { /* ignore */ }

      const headers = { 'Content-Type': 'application/json' };
      if (csrfToken) headers['X-CSRF-Token'] = csrfToken;

      const response = await fetch(`${API_BASE}/api/auth/register`, {
        method: 'POST',
        headers,
        body: JSON.stringify(userData)
      });

      // Vérifier le Content-Type avant de parser
      const contentType = response.headers.get('content-type');
      let data;
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        console.error('❌ Réponse non-JSON reçue:', text.substring(0, 100));
        return { success: false, error: 'Erreur serveur. Vérifiez que le backend est démarré.' };
      }

      if (response.ok) {
        setUser(data.user);
        localStorage.setItem('token', data.token);
        // Redirect: if a post-login "next" is provided, prefer it; otherwise role-based fallback.
        try {
          const qs = new URLSearchParams(window.location.search || '');
          const nextFromQs = qs.get('next');
          const nextFromStorage = localStorage.getItem('post_login_redirect');
          const next = (nextFromQs || nextFromStorage || '').trim();
          if (next && next.startsWith('/')) {
            localStorage.removeItem('post_login_redirect');
            navigate(next, { replace: true });
          } else {
          const role = data.user?.role;
          const isSuperAdmin = (data.user?.email || '').toLowerCase() === SUPER_ADMIN_EMAIL || role === 'super_admin';
          if (isSuperAdmin) {
            navigate('/home');
          } else if (role === 'admin' || role === 'manager') {
            navigate('/dashboard');
          } else {
            navigate('/home');
          }
          }
        } catch (err) {
          console.warn('Redirect failed:', err);
        }
        return { success: true, message: data.message };
      } else {
        const msg = response.status === 403 ? (t('auth.refresh_try_again') || 'Please refresh the page and try again.') : (data.error || 'Erreur lors de l\'inscription');
        setError(msg);
        return { success: false, error: msg };
      }
    } catch (error) {
      console.error('❌ Erreur register:', error);
      const errorMsg = error.message.includes('fetch') 
        ? 'Impossible de contacter le serveur. Vérifiez que le backend est démarré sur http://localhost:3001'
        : 'Erreur de connexion au serveur';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      // keep global loading state unchanged here; forms use local loading
    }
  };

  const login = async (emailOrPhone, password) => {
    const rawId = String(emailOrPhone ?? '');
    const trimmedId = rawId.trim();
    const isEmailLogin = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedId);

    try {
      setError('');

      // Try primary backend API first (CSRF: fetch token and send if available; API verifies)
      console.log('🔄 [LOGIN] Attempting login via backend API...');
      try {
        let csrfToken = '';
        try {
          const csrfRes = await fetch(`${API_BASE}/api/auth/csrf-token`, { method: 'GET' });
          if (csrfRes.ok) {
            const csrfData = await csrfRes.json();
            csrfToken = csrfData.csrfToken || '';
          }
        } catch (e) { /* ignore */ }

        const headers = { 'Content-Type': 'application/json' };
        if (csrfToken) headers['X-CSRF-Token'] = csrfToken;

        const response = await fetch(`${API_BASE}/api/auth/login`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            email: isEmailLogin ? trimmedId.toLowerCase() : rawId,
            password
          })
        });

        // Success
        if (response.ok) {
          const data = await response.json();
          if (data.token && data.user) {
            console.log('✅ [LOGIN] Success via backend API');
            setUser(data.user);
            localStorage.setItem('token', data.token);

            // create employee profile if needed (best-effort)
            createEmployeeProfileIfNeeded(data.token, data.user);

            // Redirect: if a post-login "next" is provided, prefer it; otherwise role-based fallback.
            try {
              const role = data.user?.role;
              const isSuperAdmin = (data.user?.email || '').toLowerCase() === SUPER_ADMIN_EMAIL || role === 'super_admin';
              setSuccessMessage(t('auth.login_success'));
              const qs = new URLSearchParams(window.location.search || '');
              const nextFromQs = qs.get('next');
              const nextFromStorage = localStorage.getItem('post_login_redirect');
              const next = (nextFromQs || nextFromStorage || '').trim();
              if (next && next.startsWith('/')) {
                localStorage.removeItem('post_login_redirect');
                navigate(next, { replace: true });
              } else if (isSuperAdmin) {
                navigate('/home');
              } else if (role === 'admin' || role === 'manager') {
                navigate('/dashboard');
              } else {
                navigate('/home');
              }
            } catch (err) {
              console.warn('Redirect failed:', err);
            }

            return { success: true, user: data.user, token: data.token };
          }
        }

        // Explicitly handle common non-2xx responses and return user-friendly English messages
        const contentType = response.headers.get('content-type') || '';
        let body = null;
        try {
          if (contentType.includes('application/json')) body = await response.json();
          else body = await response.text();
        } catch (e) {
          body = null;
        }

        const serverMsg = (body && (body.error || body.message)) ? (body.error || body.message) : (typeof body === 'string' ? body : '');

        if (response.status === 401) {
          // Wrong credentials (password mismatch)
          const errMsg = t('auth.invalid_credentials');
          setError(errMsg);
          return { success: false, error: errMsg };
        }

        if (response.status === 403) {
          const errMsg = serverMsg || t('auth.refresh_try_again') || 'Please refresh the page and try again.';
          setError(errMsg);
          return { success: false, error: errMsg };
        }

        if (response.status === 404) {
          const notFoundMsg = t('auth.user_not_found');
          setError(notFoundMsg);
          return { success: false, error: notFoundMsg };
        }

        if (response.status === 503 || response.status >= 500) {
          console.warn('⚠️ [LOGIN] Backend server error:', response.status);
          throw new Error('The authentication server is temporarily unavailable. Please try again later.');
        }

        // Other client errors: use server-provided message if any, otherwise generic invalid credentials.
        const fallbackMessage = serverMsg || t('auth.invalid_credentials');
        setError(fallbackMessage);
        return { success: false, error: fallbackMessage };
      } catch (apiError) {
        // Phone login is backend-only (no Supabase phone identifier)
        if (!isEmailLogin) {
          const errMsg = t('auth.server_unavailable') || 'The authentication server is temporarily unavailable. Please try again later.';
          setError(errMsg);
          return { success: false, error: errMsg };
        }
        // If backend is unavailable (network error or server error thrown above), fall back to Supabase auth
        console.warn('⚠️ [LOGIN] Backend API unavailable, attempting Supabase fallback...', apiError.message);
      }

      // Fallback: try Supabase Auth if backend was unreachable
      console.log('🔄 [LOGIN] Attempting Supabase fallback...');
      try {
        const result = await loginWithSupabase(trimmedId.toLowerCase(), password);
        if (result.success && result.token) {
          const token = result.token;
          // try to fetch full user from backend; if not available use result.user
          try {
            const response = await fetch(`${API_BASE}/api/auth/me`, {
              headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            if (response.ok) {
              const userData = await response.json();
              if (userData.success) {
                console.log('✅ [LOGIN] Success via Supabase fallback + backend /me');
                setUser(userData.user);
                localStorage.setItem('token', token);
                createEmployeeProfileIfNeeded(token, userData.user);
                try {
                  const role = userData.user?.role;
                  const isSuperAdmin = (userData.user?.email || '').toLowerCase() === SUPER_ADMIN_EMAIL || role === 'super_admin';
                  const successText = t('auth.login_success') || 'Login successful';
                  setSuccessMessage(successText);
                  const qs = new URLSearchParams(window.location.search || '');
                  const nextFromQs = qs.get('next');
                  const nextFromStorage = localStorage.getItem('post_login_redirect');
                  const next = (nextFromQs || nextFromStorage || '').trim();
                  if (next && next.startsWith('/')) {
                    localStorage.removeItem('post_login_redirect');
                    navigate(next, { replace: true });
                  } else if (isSuperAdmin) {
                    navigate('/home');
                  } else if (role === 'admin' || role === 'manager') {
                    navigate('/dashboard');
                  } else {
                    navigate('/home');
                  }
                } catch (err) { console.warn('Redirect failed:', err); }
                return { success: true, user: userData.user, token };
              }
            }
          } catch (e) {
            console.warn('Could not fetch /auth/me after Supabase login:', e);
          }
          // use fallback user object
          console.log('✅ [LOGIN] Success via Supabase (no backend /me)');
          setUser(result.user);
          localStorage.setItem('token', token);
          createEmployeeProfileIfNeeded(token, result.user);
            try {
              const successText = t('auth.login_success') || 'Login successful';
              setSuccessMessage(successText);
              const role = result.user?.role;
              const isSuperAdmin = (result.user?.email || '').toLowerCase() === SUPER_ADMIN_EMAIL || role === 'super_admin';
              const qs = new URLSearchParams(window.location.search || '');
              const nextFromQs = qs.get('next');
              const nextFromStorage = localStorage.getItem('post_login_redirect');
              const next = (nextFromQs || nextFromStorage || '').trim();
              if (next && next.startsWith('/')) {
                localStorage.removeItem('post_login_redirect');
                navigate(next, { replace: true });
              } else if (isSuperAdmin) {
                navigate('/home');
              } else if (role === 'admin' || role === 'manager') {
                navigate('/dashboard');
              } else {
                navigate('/home');
              }
            } catch (err) { console.warn('Redirect failed:', err); }
          return { success: true, user: result.user, token };
        } else {
          // Supabase reports invalid credentials
          const supaMsg = result.error || 'Invalid email or password.';
          setError(supaMsg);
          return { success: false, error: supaMsg };
        }
      } catch (supabaseError) {
        console.warn('⚠️ [LOGIN] Supabase Auth failed:', supabaseError.message);
        setError('Invalid email or password.');
        return { success: false, error: 'Invalid email or password.' };
      }
    } catch (error) {
      console.error('❌ [LOGIN] Erreur:', error);
      let errorMsg = error.message || t('auth.invalid_credentials');
      
      // Map error types to translated messages
      if (error.message && (error.message.includes('redéploiement') || error.message.includes('temporarily unavailable') || error.message.includes('unavailable'))) {
        errorMsg = t('auth.server_unavailable') || 'The authentication server is temporarily unavailable. Please try again later.';
      } else if (error.message && error.message.includes('503')) {
        errorMsg = t('auth.server_unavailable') || 'The authentication server is temporarily unavailable. Please try again later.';
      } else if (error.message && (error.message.includes('Invalid login credentials') || error.message.includes('Invalid') || error.message.includes('Identifiants'))) {
        errorMsg = t('auth.invalid_credentials');
      }

      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      // global loading intentionally not toggled here (use local form loading)
    }
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await unregisterWebPushForCurrentUser(token);
      }
      // Call backend logout (fire-and-forget, don't block)
      if (token) {
        fetch(`${API_BASE}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }).catch(() => {});
      }
      // Sign out from Supabase if user logged in via Supabase (clears persistent session)
      try {
        await logoutWithSupabase();
      } catch (_e) {
        // Supabase may not have been used - ignore
      }
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    } finally {
      // show logout success snackbar
      const logoutMsg = t('auth.logout_success');
      setSuccessMessage(logoutMsg);
      localStorage.removeItem('token');
      setUser(null);
      setError('');
      setDashboardStats({ leaveDays: 0, hoursWorked: 0, attendanceRate: 0 });
      // Full page redirect to website
      window.location.href = '/';
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  // Refresh user from backend - deduplicated to avoid flood of /auth/me requests
  const refreshUser = React.useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token || !user) return;
    const now = Date.now();
    if (refreshInFlight.current) return;
    if (now - lastRefreshTime.current < 5000) return; // Cooldown 5s
    refreshInFlight.current = true;
    lastRefreshTime.current = now;
    try {
      const response = await fetch(`${API_BASE}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.user) setUser(data.user);
      }
    } catch (e) {
      console.warn('refreshUser failed', e);
    } finally {
      refreshInFlight.current = false;
    }
  }, [user]);

  const value = {
    user,
    loading,
    error,
    successMessage,
    showSuccess: (msg) => setSuccessMessage(msg),
    clearSuccess: () => setSuccessMessage(''),
    login,
    register,
    logout,
    getAuthHeaders,
    refreshUser,
    clearError: () => setError(''),
    isAuthenticated: !!user,
    dashboardStats,
    setDashboardStats: (next) => setDashboardStats(prev => (typeof next === 'function' ? next(prev) : { ...prev, ...next }))
  };
  // Auto-hide success snackbar after 3s
  useEffect(() => {
    if (!successMessage) return;
    const id = setTimeout(() => setSuccessMessage(''), 3000);
    return () => clearTimeout(id);
  }, [successMessage]);

  return (
    <AuthContext.Provider value={value}>
      {children}
      {/* Global success snackbar */}
      {successMessage && (
        <div style={{
          position: 'fixed',
          top: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 12000,
          background: 'linear-gradient(90deg,#10b981,#06b6d4)',
          color: 'white',
          padding: '10px 18px',
          borderRadius: 8,
          boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
          fontWeight: 700,
          maxWidth: '90%',
          textAlign: 'center'
        }}>
          {successMessage}
        </div>
      )}
    </AuthContext.Provider>
  );
};

// Hook pour les API d'employés - VERSION CORRIGÉE (non utilisé actuellement)
// eslint-disable-next-line no-unused-vars
const useEmployees = () => {
  const { getAuthHeaders } = useAuth();

  const getEmployees = async (filters = {}) => {
    try {
      const params = new URLSearchParams(filters);
      const response = await fetch(`${API_BASE}/api/employees?${params}`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('❌ Erreur getEmployees:', error);
      return { error: 'Erreur de connexion' };
    }
  };

  const createEmployee = async (employeeData) => {
    try {
      const response = await fetch(`${API_BASE}/api/employees`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(employeeData)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('❌ Erreur createEmployee:', error);
      return { error: 'Erreur de connexion' };
    }
  };

  const updateEmployee = async (id, employeeData) => {
    try {
      const response = await fetch(`${API_BASE}/api/employees/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(employeeData)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('❌ Erreur updateEmployee:', error);
      return { error: 'Erreur de connexion' };
    }
  };

  const deleteEmployee = async (id, permanent = false) => {
    try {
      const response = await fetch(`${API_BASE}/api/employees/${id}?permanent=${permanent}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('❌ Erreur deleteEmployee:', error);
      return { error: 'Erreur de connexion' };
    }
  };

  const getEmployeesStats = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/employees/stats`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('❌ Erreur getEmployeesStats:', error);
      return { error: 'Erreur de connexion' };
    }
  };

  return { getEmployees, createEmployee, updateEmployee, deleteEmployee, getEmployeesStats };
};

// Hook pour les API de pointage - VERSION CORRIGÉE
export const useAttendance = () => {
  const { getAuthHeaders } = useAuth();

  const clockIn = async (notes = '') => {
    try {
      const response = await fetch(`${API_BASE}/api/attendance/clock-in`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ notes })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('❌ Erreur clockIn:', error);
      return { error: 'Erreur de connexion' };
    }
  };

  const clockOut = async (notes = '') => {
    try {
      const response = await fetch(`${API_BASE}/api/attendance/clock-out`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ notes })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('❌ Erreur clockOut:', error);
      return { error: 'Erreur de connexion' };
    }
  };

  const getStatus = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/attendance/status`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('❌ Erreur getStatus:', error);
      return { error: 'Erreur de connexion' };
    }
  };

  const getHistory = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/attendance/history`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('❌ Erreur getHistory:', error);
      return { error: 'Erreur de connexion' };
    }
  };

  return { clockIn, clockOut, getStatus, getHistory };
};

// Composant Modal (non utilisé actuellement)
// eslint-disable-next-line no-unused-vars
const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: '#2c3e50',
        borderRadius: '15px',
        padding: '30px',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h2 style={{ color: 'white', margin: 0 }}>{title}</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#6b7280',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '0',
              width: '30px',
              height: '30px'
            }}
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

// 🚀 SUPPRESSION: Formulaire d'employé retiré (maintenant dans Employees.js)

// Composants de connexion/inscription
const LoginForm = ({ onSwitchToRegister }) => {
  const { t } = useTranslation();
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState('');
  const { login, error: authError, clearError } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalLoading(true);
    setLocalError('');
    try { clearError(); } catch (err) {}

    const trimmed = loginId.trim();
    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
    if (!trimmed) {
      setLocalError(t('auth.enter_phone_or_email') || 'Enter your phone number or email.');
      setLocalLoading(false);
      return;
    }
    if (!isEmail && trimmed.length < 3) {
      setLocalError(t('auth.enter_valid_phone_or_email') || 'Enter a valid phone number or email address.');
      setLocalLoading(false);
      return;
    }

    try {
      const result = await login(loginId, password);
      if (!result || result.success === false) {
        setLocalError(result?.error || authError || 'Erreur lors de la connexion. Veuillez réessayer.');
      }
    } catch (err) {
      console.error('Login handler unexpected error:', err);
      setLocalError(err?.message || 'Erreur inattendue. Veuillez réessayer.');
    } finally {
      setLocalLoading(false);
    }
  };

  return (
    <div style={{
      width:'100%',
      // maxWidth: '400px',
      margin: '0 auto',
      padding: '40px 20px',
      background: 'rgba(255,255,255,0.1)',
      borderRadius: '15px',
      backdropFilter: 'blur(10px)',
      boxSizing: 'border-box',
      position: 'relative',
      zIndex: 1200
    }}>
      <h2 style={{ textAlign: 'center', marginBottom: '30px', color: 'white' }}>
        🔐 {t('auth.login')}
      </h2>
      
      {(authError || localError) && (
        <div style={{
          background: '#ff6b6b',
          color: 'white',
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '20px',
          textAlign: 'center',
          position: 'relative',
          zIndex: 1500
        }}>
          {authError || localError}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '20px' }}>
          <label
            htmlFor="login-phone-or-email"
            style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: 600,
              color: 'rgba(255, 255, 255, 0.92)',
              letterSpacing: '0.02em',
            }}
          >
            {t('auth.phone_or_email_label') || 'Phone number or email'}
          </label>
          <input
            id="login-phone-or-email"
            name="loginId"
            type="text"
            autoComplete="username"
            placeholder={t('auth.phone_or_email_placeholder') || 'Phone number or email'}
            value={loginId}
            onChange={(e) => setLoginId(e.target.value)}
            required
            style={{
              width: '100%',
              padding: '15px',
              borderRadius: '8px',
              border: 'none',
              fontSize: '16px',
              background: 'rgba(255,255,255,0.9)',
              boxSizing: 'border-box',
            }}
          />
        </div>
        
        <div style={{ marginBottom: '30px' }}>
          <label
            htmlFor="login-password"
            style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: 600,
              color: 'rgba(255, 255, 255, 0.92)',
              letterSpacing: '0.02em',
            }}
          >
            {t('auth.password')}
          </label>
          <input
            id="login-password"
            name="password"
            type="password"
            autoComplete="current-password"
            placeholder={t('auth_page.password_placeholder')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            style={{
              width: '100%',
              padding: '15px',
              borderRadius: '8px',
              border: 'none',
              fontSize: '16px',
              background: 'rgba(255,255,255,0.9)',
              boxSizing: 'border-box',
            }}
          />
        </div>

        <button
          type="submit"
          disabled={localLoading}
          style={{
            width: '100%',
            padding: '15px',
            background: localLoading ? '#ccc' : '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: localLoading ? 'not-allowed' : 'pointer',
            transition: 'background 0.3s'
          }}
        >
          {localLoading ? `⏳ ${t('auth_page.connecting')}` : `🚀 ${t('auth.login')}`}
        </button>
      </form>

      <p style={{ textAlign: 'center', marginTop: '20px', color: 'white' }}>
        {t('auth.no_account')} 
        <button
          onClick={onSwitchToRegister}
          style={{
            background: 'none',
            border: 'none',
            color: '#61dafb',
            textDecoration: 'underline',
            cursor: 'pointer',
            marginLeft: '5px'
          }}
        >
          {t('auth.sign_up')}
        </button>
      </p>
    </div>
  );
};

const RegisterForm = ({ onSwitchToLogin }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    companyName: '',
    companySize: '',
    industry: '',
    phone: '',
    message: ''
  });
  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalLoading(true);
    setLocalError('');

    // Validation côté client
    if (!formData.email || !formData.password || !formData.firstName || !formData.lastName || !formData.companyName) {
      setLocalError('Veuillez remplir tous les champs obligatoires');
      setLocalLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setLocalError('Le mot de passe doit contenir au moins 6 caractères');
      setLocalLoading(false);
      return;
    }

    // Validation email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setLocalError('Veuillez entrer une adresse email valide');
      setLocalLoading(false);
      return;
    }

    try {
      console.log('📤 Envoi demande d\'inscription...', { email: formData.email, companyName: formData.companyName });

      let csrfToken = '';
      try {
        const csrfRes = await fetch(`${API_BASE}/api/auth/csrf-token`, { method: 'GET' });
        if (csrfRes.ok) {
          const csrfData = await csrfRes.json();
          csrfToken = csrfData.csrfToken || '';
        }
      } catch (e) { /* ignore */ }

      const headers = { 'Content-Type': 'application/json' };
      if (csrfToken) headers['X-CSRF-Token'] = csrfToken;

      const response = await fetch(`${API_BASE}/api/auth/register-request`, {
        method: 'POST',
        headers,
        body: JSON.stringify(formData)
      });

      // Vérifier le Content-Type avant de parser
      const contentType = response.headers.get('content-type');
      let data;
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        console.error('❌ Réponse non-JSON reçue:', text.substring(0, 200));
        throw new Error('Erreur serveur. Vérifiez que le backend est démarré.');
      }

      if (!response.ok) {
        const errMsg = response.status === 403
          ? (t('auth.refresh_try_again') || 'Please refresh the page and try again.')
          : (data.error || data.message || t('auth_page.register_error') || 'Erreur lors de l\'envoi de la demande');
        throw new Error(errMsg);
      }

      // Succès - show French + English + pending-approval notice in English
      alert(`✅ ${data.message || "Demande d'inscription créée avec succès"}\n\n${t('auth_page.register_success') || 'Votre demande sera examinée par un administrateur. Vous recevrez un email de confirmation une fois approuvée.'}\n\nYour request has been sent to our team. You will receive a confirmation email within 24-48 hours.\n\nYour account is pending approval. You will be able to sign in once an administrator approves your account.\n\nID de demande: ${data.requestId || 'N/A'}`);
      
      // Réinitialiser le formulaire
      setFormData({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        companyName: '',
        companySize: '',
        industry: '',
        phone: '',
        message: ''
      });
      
      // Rediriger vers la page d'accueil après 2 secondes
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);

    } catch (error) {
      console.error('❌ Erreur inscription:', error);
      setLocalError(error.message || 'Erreur lors de l\'envoi de la demande. Veuillez réessayer.');
    } finally {
      setLocalLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div style={{
      // width:'100%',
      // maxWidth: {lg:'400px', md:'400px', sm:'100%', xs:'100%'},
      margin: '0 auto',
      padding: '40px 20px',
      background: 'rgba(255,255,255,0.1)',
      borderRadius: '15px',
      backdropFilter: 'blur(10px)'
    }}>
      <h2 style={{ textAlign: 'center', marginBottom: '30px', color: 'white' }}>
        ✨ {t('auth_page.register_title')}
      </h2>

      {localError && (
        <div style={{
          background: '#ff6b6b',
          color: 'white',
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '20px',
          textAlign: 'center'
        }}>
          {localError}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="name-row" style={{ display: 'flex', width: '100%', gap: '10px', marginBottom: '20px' }}>
          <input
            type="text"
            name="firstName"
            placeholder={t('auth_page.first_name')}
            value={formData.firstName}
            onChange={handleChange}
            required
            style={{
              flex: 1,
              padding: '15px',
              borderRadius: '8px',
              border: 'none',
              fontSize: '16px',
              background: 'rgba(255,255,255,0.9)',
              boxSizing: 'border-box'
            }}
          />
          <input
            type="text"
            name="lastName"
            placeholder={t('auth_page.last_name')}
            value={formData.lastName}
            onChange={handleChange}
            required
            style={{
              flex: 1,
              padding: '15px',
              borderRadius: '8px',
              border: 'none',
              fontSize: '16px',
              background: 'rgba(255,255,255,0.9)',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <input
            type="text"
            name="companyName"
            placeholder={t('auth_page.company_name')}
            value={formData.companyName}
            onChange={handleChange}
            required
            style={{
              width: '100%',
              padding: '15px',
              borderRadius: '8px',
              border: 'none',
              fontSize: '16px',
              background: 'rgba(255,255,255,0.9)',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <input
            type="email"
            name="email"
            placeholder={t('auth_page.email_professional')}
            value={formData.email}
            onChange={handleChange}
            required
            style={{
              width: '100%',
              padding: '15px',
              borderRadius: '8px',
              border: 'none',
              fontSize: '16px',
              background: 'rgba(255,255,255,0.9)',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <div style={{ marginBottom: '30px' }}>
          <input
            type="password"
            name="password"
            placeholder={t('auth_page.password_min')}
            value={formData.password}
            onChange={handleChange}
            required
            style={{
              width: '100%',
              padding: '15px',
              borderRadius: '8px',
              border: 'none',
              fontSize: '16px',
              background: 'rgba(255,255,255,0.9)',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <input
            type="tel"
            name="phone"
            placeholder={t('auth_page.phone_optional')}
            value={formData.phone}
            onChange={handleChange}
            style={{
              width: '100%',
              padding: '15px',
              borderRadius: '8px',
              border: 'none',
              fontSize: '16px',
              background: 'rgba(255,255,255,0.9)'
            }}
          />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <select
            name="companySize"
            value={formData.companySize}
            onChange={handleChange}
            style={{
              width: '100%',
              padding: '15px',
              borderRadius: '8px',
              border: 'none',
              fontSize: '16px',
              background: 'rgba(255,255,255,0.9)',
              color: formData.companySize ? '#000' : '#999',
              boxSizing: 'border-box'
            }}
          >
            <option value="">{t('auth_page.company_size')}</option>
            <option value="1-5">{t('auth_page.company_size_1_5')}</option>
            <option value="6-20">{t('auth_page.company_size_6_20')}</option>
            <option value="21-50">{t('auth_page.company_size_21_50')}</option>
            <option value="51-100">{t('auth_page.company_size_51_100')}</option>
            <option value="101-500">{t('auth_page.company_size_101_500')}</option>
            <option value="500+">{t('auth_page.company_size_500_plus')}</option>
          </select>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <select
            name="industry"
            value={formData.industry}
            onChange={handleChange}
            style={{
              width: '100%',
              padding: '15px',
              borderRadius: '8px',
              border: 'none',
              fontSize: '16px',
              background: 'rgba(255,255,255,0.9)',
              color: formData.industry ? '#000' : '#999',
              boxSizing: 'border-box'
            }}
          >
            <option value="">{t('auth_page.industry')}</option>
            <option value="Technologie">{t('auth_page.industry_tech')}</option>
            <option value="Services">{t('auth_page.industry_services')}</option>
            <option value="Commerce">{t('auth_page.industry_commerce')}</option>
            <option value="Industrie">{t('auth_page.industry_industry')}</option>
            <option value="Santé">{t('auth_page.industry_health')}</option>
            <option value="Éducation">{t('auth_page.industry_education')}</option>
            <option value="Finance">{t('auth_page.industry_finance')}</option>
            <option value="Construction">{t('auth_page.industry_construction')}</option>
            <option value="Autre">{t('auth_page.industry_other')}</option>
          </select>
        </div>

        <div style={{ marginBottom: '30px' }}>
          <textarea
            name="message"
            placeholder={t('auth_page.message_optional')}
            value={formData.message}
            onChange={handleChange}
            rows="3"
            style={{
              width: '100%',
              padding: '15px',
              borderRadius: '8px',
              border: 'none',
              fontSize: '16px',
              background: 'rgba(255,255,255,0.9)',
              resize: 'vertical',
              fontFamily: 'inherit',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <button
          type="submit"
          disabled={localLoading}
          style={{
            width: '100%',
            padding: '15px',
            background: localLoading ? '#ccc' : '#2196F3',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: localLoading ? 'not-allowed' : 'pointer',
            transition: 'background 0.3s'
          }}
        >
          {localLoading ? `⏳ ${t('auth_page.creating')}` : `🎉 ${t('auth_page.create_account')}`}
        </button>
      </form>

      <p style={{ textAlign: 'center', marginTop: '20px', color: 'white' }}>
        {t('auth_page.already_registered')} 
        <button
          onClick={onSwitchToLogin}
          style={{
            background: 'none',
            border: 'none',
            color: '#61dafb',
            textDecoration: 'underline',
            cursor: 'pointer',
            marginLeft: '5px'
          }}
        >
          {t('auth.login')}
        </button>
      </p>
    </div>
  );
};

// ✅ Module de pointage simple supprimé - Remplacé par la page d'accueil mobile-first
// ✅ AttendanceHistory supprimé - Maintenant géré dans AppRoutes.js

// Ancien composant Dashboard supprimé - Remplacé par DashboardLayout + AppRoutes (React Router)
// Ce composant utilisait activeTab/setActiveTab et ne permettait pas d'URLs distinctes
// Maintenant, la navigation est gérée par React Router dans AppRoutes.js

// Composant principal
function App() {
  const [apiStatus, setApiStatus] = useState('Vérification...');
  const [showRegister, setShowRegister] = useState(false);

  useEffect(() => {
    // Vérifier le statut du backend avec gestion robuste des erreurs
    fetch(`${API_BASE}/api/health`)
      .then(async (response) => {
        // Vérifier le Content-Type avant de parser
        const contentType = response.headers.get('content-type');
        
        if (response.ok && contentType && contentType.includes('application/json')) {
          try {
            await response.json();
        setApiStatus('✅ Backend connecté !');
          } catch (error) {
            console.error('❌ Erreur parsing réponse health:', error);
            setApiStatus('⚠️ Backend accessible mais réponse invalide');
          }
        } else {
          // Si ce n'est pas du JSON ou si la réponse n'est pas OK
          const text = await response.text();
          console.warn('⚠️ Réponse health non-JSON:', text.substring(0, 100));
          
          if (response.status === 200) {
            // Backend répond mais pas en JSON (peut-être du HTML)
            setApiStatus('⚠️ Backend accessible (réponse non-JSON)');
          } else {
            setApiStatus(`⚠️ Backend: HTTP ${response.status}`);
          }
        }
      })
      .catch(error => {
        console.error('❌ Erreur connexion backend:', error);
        setApiStatus('❌ Backend non accessible');
      });
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <div className="App">
          <PWAInstallBanner />
          <header className="App-header">
            <AuthContent 
              apiStatus={apiStatus}
              showRegister={showRegister}
              setShowRegister={setShowRegister}
            />
          </header>
        </div>
      </AuthProvider>
    </ErrorBoundary>
  );
}

// Simple Error Boundary to prevent white-screen on runtime errors
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('Uncaught error caught by ErrorBoundary:', error, info);
  }

  render() {
  if (this.state.hasError) {
      // Show a non-blocking banner but continue rendering children to avoid white-screen.
      return (
        <>
          <div style={{ position: 'fixed', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 9999, background: '#fee2e2', color: '#991b1b', padding: '10px 16px', borderRadius: 8, boxShadow: '0 6px 20px rgba(0,0,0,0.2)', maxWidth: '90%' }}>
            <strong>Une erreur est survenue:</strong> {String(this.state.error).split('\n')[0]}
            <button onClick={() => this.setState({ hasError: false, error: null })} style={{ marginLeft: 12, background: 'transparent', border: 'none', color: '#991b1b', cursor: 'pointer' }}>Dismiss</button>
          </div>
          {this.props.children}
        </>
      );
    }

    return this.props.children;
  }
}

// Contenu conditionnel selon l'état d'authentification
// REMPLACEZ le composant AuthContent dans frontend/src/App.js par :

// REMPLACEZ le composant AuthContent dans frontend/src/App.js par ce code complet :

const PUBLIC_PATHS = ['/', '/login', '/documentation', '/contact', '/faq', '/cgu', '/confidentialite', '/mentions-legales'];

const AuthContent = ({ apiStatus, showRegister, setShowRegister }) => {
  const { user, loading, showSuccess } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // When token expired or user accesses protected page: show "Need to login" in snackbar (no alert)
  const needLoginShownRef = React.useRef(false);
  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    const needLogin = location.state?.needLogin || qs.get('needLogin') === '1';
    const showAuth = qs.get('auth') === 'true' || location.pathname === '/login';
    if (!needLoginShownRef.current && needLogin && showAuth && typeof showSuccess === 'function') {
      needLoginShownRef.current = true;
      const msg = (typeof t === 'function' && t('auth.need_to_login')) ? t('auth.need_to_login') : 'Token expired. Please login.';
      showSuccess(msg);
    }
  }, [location.search, location.pathname, location.state, t, showSuccess]);

  // Dashboard/app pages: use dark background on html/body so no light bar at top or on scrollbar
  useEffect(() => {
    if (user) {
      document.documentElement.classList.add('app-dashboard');
      document.body.classList.add('app-dashboard');
    } else {
      document.documentElement.classList.remove('app-dashboard');
      document.body.classList.remove('app-dashboard');
    }
    return () => {
      document.documentElement.classList.remove('app-dashboard');
      document.body.classList.remove('app-dashboard');
    };
  }, [user]);

  // Static public pages with dark theme (legal notice, etc.): one solid background, no white strip
  const isDarkStaticPage = ['/mentions-legales', '/confidentialite', '/cgu'].includes(location?.pathname || '');
  useEffect(() => {
    if (!isDarkStaticPage) return;
    const bg = 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)';
    document.body.style.background = bg;
    document.body.style.minHeight = '100vh';
    document.documentElement.style.background = bg;
    return () => {
      document.body.style.background = '';
      document.body.style.minHeight = '';
      document.documentElement.style.background = '';
    };
  }, [isDarkStaticPage]);

  // CSS Responsive injecté dynamiquement
  useEffect(() => {
    const responsiveStyles = `
      /* Name row: default horizontal, stack on small screens */
      .name-row { display: flex; gap: 10px; }

      /* Desktop sign-in header: center title/subtitle only */
      @media (min-width: 769px) {
        .auth-header-title,
        .auth-header-subtitle {
          text-align: center;
        }
      }
      /* Responsive pour la page de connexion */
      @media (max-width: 768px) {
        .main-header .header-content {
          flex-direction: column;
          gap: 10px;
          text-align: center;
        }
        
        .company-title {
          font-size: 1.5rem !important;
        }
        
        .user-info {
          font-size: 0.9rem;
        }
        
        .nav-tabs {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          justify-content: center;
          padding: 10px;
        }
        
        .nav-tab {
          font-size: 0.8rem !important;
          padding: 8px 12px !important;
          min-width: auto;
          flex: none;
        }
        
        /* Responsive pour auth container */
        .auth-main-container {
          padding: 10px !important;
        }
        
        .auth-grid {
          grid-template-columns: 1fr !important;
          gap: 20px !important;
          max-width: 400px;
          margin: 0 auto;
        }
        
        .auth-header {
          padding: 10px !important;
          margin-bottom: 15px !important;
        }
        
        /* Mobile sign-in header: row1 = home (left) + language (right), row2 = title, row3 = subtext */
        .auth-header-inner {
          display: flex;
          flex-direction: column;
        }
        .auth-header-topbar {
          display: flex !important;
          flex-wrap: nowrap;
          justify-content: space-between;
          align-items: center;
          order: 1;
          margin-bottom: 12px !important;
          min-width: 0;
        }
        .auth-header-topbar > a {
          flex-shrink: 0;
        }
        .auth-header-lang {
          flex-shrink: 0;
          min-width: 0;
        }
        .auth-header-title {
          order: 2;
          width: 100%;
        }
        .auth-header-subtitle {
          order: 3;
          width: 100%;
        }
        
        .auth-title {
          font-size: 1.8rem !important;
        }
        
        .auth-subtitle {
          font-size: 1rem !important;
        }
        
        .auth-presentation {
          order: 2;
          padding: 20px !important;
        }
        
        .auth-form-section {
          order: 1;
          padding: 20px !important;
        }
        
        .auth-features {
          gap: 15px !important;
        }
        
        .auth-feature {
          flex-direction: row !important;
          text-align: left !important;
          gap: 12px !important;
        }
        
        .auth-feature-icon {
          width: 35px !important;
          height: 35px !important;
          font-size: 16px !important;
          flex-shrink: 0;
        }
        
        .auth-feature h4 {
          font-size: 0.95rem !important;
          margin-bottom: 4px !important;
        }
        
        .auth-feature p {
          font-size: 0.85rem !important;
        }
        
        /* Stack first/last name on small screens */
        .name-row { flex-direction: column !important; }
        .name-row input { width: 100% !important; }

        .auth-footer {
          padding: 10px !important;
          margin-top: 15px !important;
        }
      }
      
      @media (max-width: 480px) {
        .company-title {
          font-size: 1.2rem !important;
        }
        
        .nav-tab {
          font-size: 0.7rem !important;
          padding: 6px 8px !important;
        }
        
        .auth-main-container {
          padding: 5px !important;
        }
        
        .auth-grid {
          max-width: 100% !important;
        }
        
        .auth-presentation,
        .auth-form-section {
          padding: 15px !important;
        }
        
        /* Keep topbar on one line on small mobile */
        .auth-header-topbar > a {
          width: 44px !important;
          height: 44px !important;
        }
        
        .auth-title {
          font-size: 1.5rem !important;
        }
        
        .auth-subtitle {
          font-size: 0.9rem !important;
        }
        
        .auth-feature {
          flex-direction: column !important;
          text-align: center !important;
          gap: 8px !important;
        }
        
        .auth-feature-icon {
          align-self: center;
        }
        
        input, textarea, select {
          font-size: 16px !important;
        }
      }
    `;

    const styleId = 'workbiflow-auth-responsive-styles';
    let created = false;

    // If the style tag doesn't already exist, create and append it.
    let styleElement = document.getElementById(styleId);
    if (!styleElement) {
      try {
        styleElement = document.createElement('style');
        styleElement.id = styleId;
        styleElement.textContent = responsiveStyles;
        document.head.appendChild(styleElement);
        created = true;
      } catch (err) {
        console.warn('Unable to append responsive styles to document.head:', err);
      }
    } else {
      // If it exists, ensure it has the latest content (helps during HMR/dev)
      try {
        styleElement.textContent = responsiveStyles;
      } catch (err) {
        console.warn('Unable to update responsive styles content:', err);
      }
    }

    return () => {
      try {
        const el = document.getElementById(styleId);
        // Only remove if this effect created it and it's still attached
        if (created && el && el.parentNode) {
          el.parentNode.removeChild(el);
        }
      } catch (err) {
        console.warn('Error while cleaning up responsive styles:', err);
      }
    };
  }, []);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        flexDirection: 'column',
        gap: '20px',
        padding: '20px'
      }}>
        <div style={{
          width: '60px',
          height: '60px',
          border: '4px solid rgba(255,255,255,0.3)',
          borderTop: '4px solid white',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <div style={{ color: 'white', fontSize: '18px', textAlign: 'center' }}>
          {t('common.loading')} WorkbiFlow...
        </div>
      </div>
    );
  }

  // Si utilisateur connecté, utiliser AppRoutes
  if (user) {
    return <AppRoutes />;
  }

  // Routes pour utilisateurs non authentifiés
  const urlParams = new URLSearchParams(window.location.search);
  const showAuth = urlParams.get('auth') === 'true' || window.location.pathname === '/login';
  const pathname = window.location.pathname;

  // Token manquant/expiré ou accès à une page protégée : rediriger vers la page d'auth avec "next"
  if (!loading && !PUBLIC_PATHS.includes(pathname)) {
    try {
      const next = encodeURIComponent(`${pathname || '/'}${window.location.search || ''}`);
      localStorage.setItem('post_login_redirect', `${pathname || '/'}${window.location.search || ''}`);
      navigate(`/?auth=true&needLogin=1&next=${next}`, { replace: true });
    } catch (_e) {
      navigate('/?auth=true&needLogin=1', { replace: true });
    }
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)', color: '#fff' }}>
        {t('common.loading') || 'Loading...'}
      </div>
    );
  }
  
  // Routes publiques statiques
  if (pathname === '/documentation') {
    return <DocumentationPage />;
  }
  if (pathname === '/contact') {
    return <ContactPage />;
  }
  if (pathname === '/faq') {
    return <FAQPage />;
  }
  if (pathname === '/cgu') {
    return <CGUPage />;
  }
  if (pathname === '/confidentialite') {
    return <ConfidentialityPage />;
  }
  if (pathname === '/mentions-legales') {
    return <LegalNoticePage />;
  }
  
  if (!showAuth) {
    return <LandingPage onNavigate={(tab) => {
      if (tab === 'login' || tab === 'register') {
        window.location.href = `/?auth=true${tab === 'register' ? '&register=true' : ''}`;
      }
    }} />;
  }
  return (
    <div className="auth-main-container" style={{
      minHeight: '100vh',
      width: '100%',
      background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
      display: 'flex',
      flexDirection: 'column',
      padding: '20px',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflowY: 'auto'
    }}>
      {/* Header: desktop = one row (home, title+subtitle, language); mobile = row1: home+language, row2: title, row3: subtitle */}
      <div className="auth-header" style={{
        background: 'rgba(255,255,255,0.1)',
        backdropFilter: 'blur(10px)',
        padding: '20px',
        borderRadius: '15px',
        marginBottom: '30px',
        border: '1px solid rgba(255,255,255,0.2)'
      }}>
        <div className="auth-header-inner" style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 10px'
        }}>
          {/* Mobile: one row - home left, language right */}
          <div className="auth-header-topbar" style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            marginBottom: '12px'
          }}>
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                window.location.replace('/');
              }}
              title={t('common.home') || t('auth.back_to_landing') || 'Back to landing page'}
              aria-label={t('common.home') || 'Home'}
              style={{
                width: '50px',
                height: '50px',
                background: 'rgba(255,255,255,0.1)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.2)',
                textDecoration: 'none',
                flexShrink: 0,
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <Home size={24} />
            </a>
            <div className="auth-header-lang" style={{ flexShrink: 0 }}>
              <LanguageSelector style={{
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.2)',
                padding: '8px 12px',
                borderRadius: '10px',
                backdropFilter: 'blur(10px)'
              }} />
            </div>
          </div>
          {/* Title on its own line */}
          <h1 className="auth-title auth-header-title" style={{ 
            margin: 0, 
            color: 'white', 
            fontSize: '2.5rem',
            fontWeight: 'bold',
            lineHeight: 1.2
          }}>
            WorkbiFlow
          </h1>
          {/* Subtitle on new line */}
          <p className="auth-subtitle auth-header-subtitle" style={{ 
            margin: 0, 
            marginTop: '6px',
            color: 'rgba(255,255,255,0.8)', 
            fontSize: '1.2rem'
          }}>
            {t('landing.subtitle')}
          </p>
        </div>
      </div>

      {/* Contenu principal responsive */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div className="auth-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(12, 1fr)', // 12-column grid: each child will span 6 columns on desktop
          gap: '50px',
          maxWidth: '1200px',
          width: '100%',
          alignItems: 'center',
          boxSizing: 'border-box'
        }}>
          {/* Section présentation */}
          <div className="auth-presentation" style={{ order: showRegister ? 2 : 1, gridColumn: 'span 6' }}>
            <div style={{
              background: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(15px)',
              borderRadius: '20px',
              padding: '40px',
              border: '1px solid rgba(255,255,255,0.2)'
            }}>
              <h2 style={{
                color: 'white',
                fontSize: '2rem',
                marginBottom: '25px',
                textAlign: 'center'
              }}>
                ✨ {t('auth_page.why_choose')}
              </h2>
              
              <div className="auth-features" style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
                <div 
                  className="auth-feature" 
                  style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: '15px',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateX(5px)';
                    const icon = e.currentTarget.querySelector('.auth-feature-icon');
                    if (icon) {
                      icon.style.transform = 'scale(1.15) rotate(5deg)';
                      icon.style.transition = 'all 0.3s ease';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateX(0)';
                    const icon = e.currentTarget.querySelector('.auth-feature-icon');
                    if (icon) {
                      icon.style.transform = 'scale(1) rotate(0deg)';
                    }
                  }}
                >
                  <div className="auth-feature-icon" style={{
                    width: '50px',
                    height: '50px',
                    background: 'rgba(0, 212, 255, 0.2)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    transition: 'all 0.3s ease'
                  }}>
                    <Clock size={24} color="#00d4ff" />
                  </div>
                  <div>
                    <h4 style={{ color: 'white', margin: '0 0 8px 0', fontSize: '1.1rem' }}>
                      {t('auth_page.feature_pointage_title')}
                    </h4>
                    <p style={{ color: 'rgba(255,255,255,0.8)', margin: 0, fontSize: '0.95rem', lineHeight: 1.5 }}>
                      {t('auth_page.feature_pointage_desc')}
                    </p>
                  </div>
                </div>

                <div 
                  className="auth-feature" 
                  style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: '15px',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateX(5px)';
                    const icon = e.currentTarget.querySelector('.auth-feature-icon');
                    if (icon) {
                      icon.style.transform = 'scale(1.15) rotate(-5deg)';
                      icon.style.transition = 'all 0.3s ease';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateX(0)';
                    const icon = e.currentTarget.querySelector('.auth-feature-icon');
                    if (icon) {
                      icon.style.transform = 'scale(1) rotate(0deg)';
                    }
                  }}
                >
                  <div className="auth-feature-icon" style={{
                    width: '50px',
                    height: '50px',
                    background: 'rgba(33, 150, 243, 0.2)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    transition: 'all 0.3s ease'
                  }}>
                    <BarChart3 size={24} color="#2196f3" />
                  </div>
                  <div>
                    <h4 style={{ color: 'white', margin: '0 0 8px 0', fontSize: '1.1rem' }}>
                      {t('auth_page.feature_analytics_title')}
                    </h4>
                    <p style={{ color: 'rgba(255,255,255,0.8)', margin: 0, fontSize: '0.95rem', lineHeight: 1.5 }}>
                      {t('auth_page.feature_analytics_desc')}
                    </p>
                  </div>
                </div>

                <div 
                  className="auth-feature" 
                  style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: '15px',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateX(5px)';
                    const icon = e.currentTarget.querySelector('.auth-feature-icon');
                    if (icon) {
                      icon.style.transform = 'scale(1.15) rotate(5deg)';
                      icon.style.transition = 'all 0.3s ease';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateX(0)';
                    const icon = e.currentTarget.querySelector('.auth-feature-icon');
                    if (icon) {
                      icon.style.transform = 'scale(1) rotate(0deg)';
                    }
                  }}
                >
                  <div className="auth-feature-icon" style={{
                    width: '50px',
                    height: '50px',
                    background: 'rgba(16, 185, 129, 0.2)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    transition: 'all 0.3s ease'
                  }}>
                    <Users size={24} color="#10b981" />
                  </div>
                  <div>
                    <h4 style={{ color: 'white', margin: '0 0 8px 0', fontSize: '1.1rem' }}>
                      {t('auth_page.feature_management_title')}
                    </h4>
                    <p style={{ color: 'rgba(255,255,255,0.8)', margin: 0, fontSize: '0.95rem', lineHeight: 1.5 }}>
                      {t('auth_page.feature_management_desc')}
                    </p>
                  </div>
                </div>

                <div 
                  className="auth-feature" 
                  style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: '15px',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateX(5px)';
                    const icon = e.currentTarget.querySelector('.auth-feature-icon');
                    if (icon) {
                      icon.style.transform = 'scale(1.15) rotate(-5deg)';
                      icon.style.transition = 'all 0.3s ease';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateX(0)';
                    const icon = e.currentTarget.querySelector('.auth-feature-icon');
                    if (icon) {
                      icon.style.transform = 'scale(1) rotate(0deg)';
                    }
                  }}
                >
                  <div className="auth-feature-icon" style={{
                    width: '50px',
                    height: '50px',
                    background: 'rgba(156, 39, 176, 0.2)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    transition: 'all 0.3s ease'
                  }}>
                    <Shield size={24} color="#9c27b0" />
                  </div>
                  <div>
                    <h4 style={{ color: 'white', margin: '0 0 8px 0', fontSize: '1.1rem' }}>
                      {t('auth_page.feature_security_title')}
                    </h4>
                    <p style={{ color: 'rgba(255,255,255,0.8)', margin: 0, fontSize: '0.95rem', lineHeight: 1.5 }}>
                      {t('auth_page.feature_security_desc')}
                    </p>
                  </div>
                </div>
              </div>

              <div style={{
                marginTop: '30px',
                padding: '25px',
                background: 'rgba(76, 175, 80, 0.15)',
                borderRadius: '15px',
                border: '1px solid rgba(76, 175, 80, 0.3)',
                textAlign: 'center'
              }}>
                <div style={{ color: '#4CAF50', fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '10px' }}>
                  🚀 {t('auth_page.immediate_start')}
                </div>
                <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: '1rem', lineHeight: 1.4 }}>
                  {t('auth_page.setup_support')}
                </div>
              </div>
            </div>
          </div>

          {/* Section formulaires */}
          <div className="auth-form-section" style={{ order: showRegister ? 1 : 2, gridColumn: 'span 6' }}>
            {showRegister ? (
              <RegisterForm onSwitchToLogin={() => setShowRegister(false)} />
            ) : (
              <LoginForm onSwitchToRegister={() => setShowRegister(true)} />
            )}
          </div>
        </div>
      </div>

      {/* Footer responsive */}
      <footer className="auth-footer" style={{
        background: 'rgba(0,0,0,0.2)',
        padding: '20px',
        textAlign: 'center',
        borderRadius: '15px',
        marginTop: '30px',
        border: '1px solid rgba(255,255,255,0.1)'
      }}>
        <div style={{
          color: 'rgba(255,255,255,0.7)',
          fontSize: '0.9rem'
        }}>
          {t('landing.footer_copyright')} | 
          <span style={{ margin: '0 10px' }}>•</span>
          {t('auth_page.footer_made_with')}
        </div>
      </footer>
    </div>
  );
};
export default App;